import { useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

export interface UseTableNavigationOptions {
  containerRef?: React.RefObject<HTMLElement | null>;
}

export interface UseTableNavigationReturn {
  navigateToDetail: (path: string, state?: Record<string, unknown> & { returnSearchParams?: string }) => void;
  getSearchParamsString: () => string;
  restoreScrollPosition: () => void;
}

export function useTableNavigation({ containerRef }: UseTableNavigationOptions = {}): UseTableNavigationReturn {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const getSearchParamsString = useCallback(() => {
    return new URLSearchParams(searchParams).toString();
  }, [searchParams]);

  const navigateToDetail = useCallback(
    (path: string, state?: Record<string, unknown> & { returnSearchParams?: string }) => {
      const params = new URLSearchParams(searchParams);

      if (containerRef?.current) {
        const scrollTop = containerRef.current.scrollTop;
        if (scrollTop > 0) {
          params.set('scrollTop', scrollTop.toString());
        }
      }

      const updatedParams = params.toString();

      if (updatedParams) {
        const currentUrl = `${window.location.pathname}?${updatedParams}`;
        window.history.replaceState({}, '', currentUrl);
      }

      const navigationState = {
        ...state,
        returnSearchParams: updatedParams,
      };

      navigate(path, { state: navigationState });
    },
    [navigate, searchParams, containerRef],
  );

  const restoreScrollPosition = useCallback(() => {
    if (!containerRef?.current) return;

    let scrollTopParam = searchParams.get('scrollTop');

    if (!scrollTopParam) {
      const state = location.state as { returnSearchParams?: string } | undefined;
      const returnSearchParams = state?.returnSearchParams;
      if (returnSearchParams) {
        const params = new URLSearchParams(returnSearchParams);
        scrollTopParam = params.get('scrollTop');
      }
    }

    if (scrollTopParam) {
      const scrollTop = parseInt(scrollTopParam, 10);
      if (!isNaN(scrollTop) && scrollTop > 0) {
        const el = containerRef.current;
        if (el) {
          const attemptRestore = (attempt = 0) => {
            const currentEl = containerRef?.current;
            if (!currentEl) {
              if (attempt < 10) {
                setTimeout(() => attemptRestore(attempt + 1), 100);
              }
              return;
            }

            const hasContent = currentEl.scrollHeight > 0;
            const hasScrollableContent = currentEl.scrollHeight > currentEl.clientHeight;
            if (hasContent && (hasScrollableContent || attempt >= 5)) {
              if (Math.abs(currentEl.scrollTop - scrollTop) > 1) {
                currentEl.scrollTo({ top: scrollTop, behavior: 'auto' });
              }
              const params = new URLSearchParams(window.location.search);
              params.delete('scrollTop');
              const newUrl = params.toString() ? `${window.location.pathname}?${params.toString()}` : window.location.pathname;
              window.history.replaceState({}, '', newUrl);
            } else if (attempt < 10) {
              setTimeout(() => attemptRestore(attempt + 1), 100);
            }
          };

          requestAnimationFrame(() => {
            attemptRestore(0);
          });
        }
      }
    }
  }, [searchParams, location.state, containerRef]);

  return {
    navigateToDetail,
    getSearchParamsString,
    restoreScrollPosition,
  };
}
